import React, { useEffect, useMemo, useState, Suspense } from "react";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, TrendingUp, Users, Map as MapIcon, RefreshCcw, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LeafletHeatmap = React.lazy(() => import("./LeafletHeatmap"));
const ActivationTestCard = React.lazy(() => import("./ActivationTestCard"));
const LaunchChecklistCard = React.lazy(() => import("./LaunchChecklistCard"));

const fmt$ = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const EXPORT_COLLECTIONS = [
    { key: "users", label: "Users" },
    { key: "family_charges", label: "Charges" },
    { key: "broadcasts", label: "Broadcasts" },
    { key: "broadcast_recipients", label: "Broadcast recipients" },
    { key: "apollo_leads", label: "Apollo leads" },
    { key: "visitors", label: "Visitors" },
    { key: "referral_claims", label: "Referral claims" },
    { key: "audit_log", label: "Audit log" },
];

async function fetchZipCentroid(zip) {
    try {
        const r = await fetch(`https://api.zippopotam.us/us/${zip}`);
        if (!r.ok) return null;
        const j = await r.json();
        const p = j.places?.[0];
        if (!p) return null;
        return { lat: parseFloat(p.latitude), lng: parseFloat(p.longitude), city: p["place name"], state: p["state abbreviation"] };
    } catch {
        return null;
    }
}

function heatColor(rate) {
    // 0% red → 100% green
    const clamped = Math.min(100, Math.max(0, rate || 0));
    const hue = Math.round((clamped / 100) * 120);
    return `hsl(${hue}, 70%, 45%)`;
}

export default function AnalyticsPanel() {
    return (
        <div className="space-y-6">
            <Suspense fallback={null}>
                <LaunchChecklistCard />
            </Suspense>
            <Suspense fallback={null}>
                <ActivationTestCard />
            </Suspense>
            <ExportBar />
            <RevenueChartCard />
            <div className="grid md:grid-cols-2 gap-5">
                <CohortCard />
                <LtvCard />
            </div>
            <MapBoundary>
                <HeatmapCard />
            </MapBoundary>
        </div>
    );
}

// Scoped error boundary — keeps the rest of the Analytics tab usable even if
// the leaflet map fails to init. Remounts children with a fresh key on retry.
class MapBoundary extends React.Component {
    constructor(props) { super(props); this.state = { error: null, retryKey: 0 }; }
    static getDerivedStateFromError(error) { return { error }; }
    componentDidCatch(err) { console.warn("HeatmapCard failed:", err?.message); }
    render() {
        if (this.state.error) {
            return (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="heatmap-error">
                    <div className="font-outfit font-semibold text-amber-900 text-sm">Heat map unavailable</div>
                    <div className="text-xs text-amber-800 mt-1 font-dmsans">
                        {String(this.state.error?.message || this.state.error)}
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        className="mt-3 h-8 rounded-full border-amber-300 bg-white"
                        onClick={() => this.setState((s) => ({ error: null, retryKey: s.retryKey + 1 }))}
                        data-testid="heatmap-retry-btn"
                    >Retry</Button>
                </div>
            );
        }
        return <React.Fragment key={this.state.retryKey}>{this.props.children}</React.Fragment>;
    }
}

// ---------- CSV Export bar ----------
function ExportBar() {
    const [busy, setBusy] = useState(null);

    const download = async (collection) => {
        setBusy(collection);
        try {
            const res = await api.get(`/admin/analytics/export/${collection}`, { responseType: "blob" });
            if (res.status === 204 || !res.data || res.data.size === 0) {
                toast.info(`No rows to export for ${collection}`);
                return;
            }
            const blob = new Blob([res.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `careco24_${collection}_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Exported ${collection}`);
        } catch (e) {
            const msg = e?.response?.status === 204 ? "No rows to export" : (e?.response?.data?.detail || "Export failed");
            toast.error(msg);
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="rounded-xl border border-stone-200 bg-stone-50 p-4">
            <div className="flex items-center gap-2 mb-2">
                <Download className="w-4 h-4 text-stone-700" />
                <div className="font-outfit font-semibold text-stone-900 text-sm">CSV exports</div>
                <span className="text-xs text-stone-500 font-dmsans">Secrets & hashes are stripped automatically.</span>
            </div>
            <div className="flex flex-wrap gap-2">
                {EXPORT_COLLECTIONS.map((c) => (
                    <Button
                        key={c.key}
                        data-testid={`export-${c.key}-btn`}
                        variant="outline"
                        size="sm"
                        disabled={busy === c.key}
                        onClick={() => download(c.key)}
                        className="rounded-full h-8 text-xs font-dmsans border-stone-300 bg-white hover:bg-stone-100"
                    >
                        {busy === c.key ? "…" : <><Download className="w-3 h-3 mr-1.5" />{c.label}</>}
                    </Button>
                ))}
            </div>
        </div>
    );
}

// ---------- Revenue timeseries ----------
function RevenueChartCard() {
    const [days, setDays] = useState(30);
    const [bucket, setBucket] = useState("day");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/analytics/revenue", { params: { days, bucket } });
            setData(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load revenue");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, bucket]);

    const chartData = useMemo(
        () => (data?.series || []).map((p) => ({ ...p, revenue: (p.revenue_cents || 0) / 100 })),
        [data]
    );

    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <div className="font-outfit font-semibold text-stone-900">Revenue</div>
                <Badge className="bg-emerald-100 text-emerald-800 rounded-full text-[10px]">{fmt$(data?.total_cents || 0)}</Badge>
                <div className="ml-auto flex items-center gap-1">
                    {["day", "week", "month"].map((b) => (
                        <button
                            key={b}
                            data-testid={`revenue-bucket-${b}`}
                            onClick={() => setBucket(b)}
                            className={`px-2.5 h-7 rounded-full text-[11px] font-dmsans ${bucket === b ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
                        >{b}</button>
                    ))}
                    <span className="mx-1 text-stone-300">|</span>
                    {[7, 30, 90, 180, 365].map((d) => (
                        <button
                            key={d}
                            data-testid={`revenue-range-${d}`}
                            onClick={() => setDays(d)}
                            className={`px-2.5 h-7 rounded-full text-[11px] font-dmsans ${days === d ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
                        >{d}d</button>
                    ))}
                    <Button size="sm" variant="ghost" onClick={load} data-testid="revenue-refresh-btn" className="h-7 px-2">
                        <RefreshCcw className="w-3.5 h-3.5" />
                    </Button>
                </div>
            </div>
            <div style={{ width: "100%", height: 260 }} data-testid="revenue-chart">
                {loading && !chartData.length ? (
                    <div className="h-full flex items-center justify-center text-xs text-stone-500 font-dmsans">Loading…</div>
                ) : !chartData.length ? (
                    <div className="h-full flex items-center justify-center text-xs text-stone-500 font-dmsans">No revenue in this window.</div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                            <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#57534e" }} />
                            <YAxis tick={{ fontSize: 11, fill: "#57534e" }} tickFormatter={(v) => `$${v}`} />
                            <Tooltip formatter={(v, name) => name === "revenue" ? [`$${v.toLocaleString()}`, "Revenue"] : [v, name]} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line type="monotone" dataKey="revenue" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} name="Revenue ($)" />
                            <Line type="monotone" dataKey="charges" stroke="#0891b2" strokeWidth={1.5} dot={{ r: 2 }} name="Charges" />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}

// ---------- Cohort retention ----------
function CohortCard() {
    const [days, setDays] = useState(60);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/analytics/cohort-retention", { params: { days } });
            setData(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load cohort");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Activity className="w-4 h-4 text-indigo-600" />
                <div className="font-outfit font-semibold text-stone-900">Caregiver cohort activation</div>
                <Badge className="bg-indigo-100 text-indigo-800 rounded-full text-[10px]">cohort {data?.cohort_size ?? "—"}</Badge>
                <div className="ml-auto flex items-center gap-1">
                    {[30, 60, 90, 180].map((d) => (
                        <button
                            key={d}
                            data-testid={`cohort-range-${d}`}
                            onClick={() => setDays(d)}
                            className={`px-2.5 h-7 rounded-full text-[11px] font-dmsans ${days === d ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
                        >{d}d</button>
                    ))}
                </div>
            </div>
            <div style={{ width: "100%", height: 220 }} data-testid="cohort-chart">
                {loading && !data ? (
                    <div className="h-full flex items-center justify-center text-xs text-stone-500 font-dmsans">Loading…</div>
                ) : !data?.buckets?.length ? (
                    <div className="h-full flex items-center justify-center text-xs text-stone-500 font-dmsans">No cohort data yet.</div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.buckets} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#57534e" }} />
                            <YAxis tick={{ fontSize: 11, fill: "#57534e" }} tickFormatter={(v) => `${v}%`} />
                            <Tooltip formatter={(v, name) => name === "pct" ? [`${v}%`, "Activated"] : [v, name]} />
                            <Bar dataKey="pct" fill="#6366f1" radius={[6, 6, 0, 0]} name="pct" />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}

// ---------- Caregiver LTV table ----------
function LtvCard() {
    const [limit] = useState(25);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await api.get("/admin/analytics/caregiver-ltv", { params: { limit } });
                setData(res.data);
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed to load LTV");
            } finally { setLoading(false); }
        })();
    }, [limit]);

    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-amber-600" />
                <div className="font-outfit font-semibold text-stone-900">Top caregivers by LTV</div>
                <Badge className="bg-amber-100 text-amber-800 rounded-full text-[10px]">top {data?.count ?? "—"}</Badge>
            </div>
            <div className="max-h-[260px] overflow-y-auto rounded-lg border border-stone-200" data-testid="ltv-table">
                <table className="w-full text-xs font-dmsans">
                    <thead className="bg-stone-100 text-stone-600 sticky top-0">
                        <tr>
                            <th className="text-left px-3 py-2">Caregiver</th>
                            <th className="text-right px-3 py-2">Hours</th>
                            <th className="text-right px-3 py-2">Visits</th>
                            <th className="text-right px-3 py-2">Avg $/hr</th>
                            <th className="text-right px-3 py-2">LTV</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="px-3 py-4 text-center text-stone-500">Loading…</td></tr>
                        ) : !data?.top?.length ? (
                            <tr><td colSpan={5} className="px-3 py-4 text-center text-stone-500">No completed visits yet.</td></tr>
                        ) : data.top.map((r) => (
                            <tr key={r.caregiver_id} className="border-t border-stone-100">
                                <td className="px-3 py-2 text-stone-800">{r.name || r.email || r.caregiver_id.slice(0, 8)}</td>
                                <td className="px-3 py-2 text-right text-stone-700">{r.hours}</td>
                                <td className="px-3 py-2 text-right text-stone-700">{r.visits}</td>
                                <td className="px-3 py-2 text-right text-stone-700">${r.avg_rate_usd}</td>
                                <td className="px-3 py-2 text-right font-medium text-emerald-700">${r.ltv_usd.toLocaleString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Broadcast heat map ----------
function HeatmapCard() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [geo, setGeo] = useState({}); // zip -> {lat,lng,city,state}
    const [geoing, setGeoing] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/analytics/broadcast-heatmap", { params: { days } });
            setData(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load heatmap");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

    // Lazy-geocode ZIPs when data changes (max 40 to be polite to zippopotam)
    useEffect(() => {
        const zips = (data?.zips || []).map((z) => z.zip).filter(Boolean).slice(0, 40);
        const missing = zips.filter((z) => !geo[z]);
        if (!missing.length) return;
        let cancelled = false;
        setGeoing(true);
        (async () => {
            const results = await Promise.all(missing.map((z) => fetchZipCentroid(z).then((c) => [z, c])));
            if (cancelled) return;
            const next = { ...geo };
            for (const [z, c] of results) if (c) next[z] = c;
            setGeo(next);
            setGeoing(false);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const markers = useMemo(() => {
        return (data?.zips || [])
            .map((z) => ({ ...z, coords: geo[z.zip] }))
            .filter((z) => z.coords);
    }, [data, geo]);

    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <MapIcon className="w-4 h-4 text-rose-600" />
                <div className="font-outfit font-semibold text-stone-900">Shift-blast response by ZIP</div>
                {geoing && <span className="text-[11px] text-stone-500 font-dmsans">geocoding…</span>}
                <div className="ml-auto flex items-center gap-1">
                    {[7, 30, 90].map((d) => (
                        <button
                            key={d}
                            data-testid={`heatmap-range-${d}`}
                            onClick={() => setDays(d)}
                            className={`px-2.5 h-7 rounded-full text-[11px] font-dmsans ${days === d ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
                        >{d}d</button>
                    ))}
                </div>
            </div>

            <div className="rounded-xl overflow-hidden border border-stone-200" style={{ height: 320 }} data-testid="heatmap-map">
                <Suspense fallback={<div className="h-full flex items-center justify-center text-xs text-stone-500 font-dmsans">Loading map…</div>}>
                    <LeafletHeatmap markers={markers} />
                </Suspense>
            </div>

            <div className="mt-3 max-h-[180px] overflow-y-auto rounded-lg border border-stone-200" data-testid="heatmap-table">
                <table className="w-full text-xs font-dmsans">
                    <thead className="bg-stone-100 text-stone-600 sticky top-0">
                        <tr>
                            <th className="text-left px-3 py-2">ZIP</th>
                            <th className="text-right px-3 py-2">Sent</th>
                            <th className="text-right px-3 py-2">Yes</th>
                            <th className="text-right px-3 py-2">No</th>
                            <th className="text-right px-3 py-2">Response rate</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="px-3 py-4 text-center text-stone-500">Loading…</td></tr>
                        ) : !data?.zips?.length ? (
                            <tr><td colSpan={5} className="px-3 py-4 text-center text-stone-500">No broadcasts yet.</td></tr>
                        ) : data.zips.map((z) => (
                            <tr key={z.zip || "unknown"} className="border-t border-stone-100">
                                <td className="px-3 py-2 text-stone-800">{z.zip || "—"}</td>
                                <td className="px-3 py-2 text-right text-stone-700">{z.sent}</td>
                                <td className="px-3 py-2 text-right text-emerald-700">{z.yes}</td>
                                <td className="px-3 py-2 text-right text-stone-500">{z.no}</td>
                                <td className="px-3 py-2 text-right">
                                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] text-white" style={{ backgroundColor: heatColor(z.response_rate) }}>
                                        {Math.round(z.response_rate)}%
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
